import {
  EXPENSE_CATEGORIES,
  EXPENSE_CATEGORY_LABELS,
  formatARS,
  PAYMENT_METHOD_LABELS,
  type ExpenseCategory,
  type ExpenseDto,
  type IncomeDto,
  type PaymentMethod,
  type SaleDto,
} from '@gl/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowDownLeft, ArrowLeftRight, ArrowUpRight, ChevronRight, ShoppingBag, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/FullScreenLoader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useCurrentLocal, useHasRole } from '@/lib/local-context';
import { cn } from '@/lib/utils';
import { PaymentMethodPicker } from './CartPanel';
import { formatTime } from './format';
import { MoneyInput } from './MoneyInput';
import { caja, cajaKeys, useInvalidateCaja } from './queries';
import { SaleDetailDialog, SaleStatusBadge } from './SaleDetail';

export function TodaySales({ businessDate, onSell }: { businessDate: string; /** Lleva a la grilla de venta. */ onSell?: () => void }) {
  const local = useCurrentLocal();
  const { user } = useAuth();
  const isManager = useHasRole('OWNER', 'MANAGER');
  const [selected, setSelected] = useState<SaleDto | null>(null);
  const sales = useQuery({
    queryKey: cajaKeys.salesOf(local.id, businessDate),
    queryFn: () => caja.listSales(local.id, { businessDate, pageSize: 500 }),
  });

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <CardTitle className="text-base">Ventas del día</CardTitle>
        {sales.data && <span className="text-sm text-muted-foreground">{sales.data.total}</span>}
      </CardHeader>
      <CardContent className="px-0">
        {sales.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : !sales.data?.data.length ? (
          <EmptyState
            compact
            icon={ShoppingBag}
            title="Todavía no hay ventas hoy"
            description="Cuando cargues una venta, la vas a ver acá con su número y medio de pago."
            action={
              onSell && (
                <Button size="sm" onClick={onSell}>
                  Cargar una venta
                </Button>
              )
            }
          />
        ) : (
          <ul className="divide-y">
            {sales.data.data.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => setSelected(s)}
                  className={cn(
                    'flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-accent/50',
                    s.status === 'VOIDED' && 'opacity-60',
                  )}
                >
                  <span className="w-10 shrink-0 text-sm font-semibold text-muted-foreground tabular-nums">#{s.number}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">
                      {s.items.map((i) => `${i.quantity}× ${i.nameSnapshot}${i.variantNameSnapshot ? ` ${i.variantNameSnapshot}` : ''}`).join(', ')}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {formatTime(s.soldAt, local.timezone)} · {PAYMENT_METHOD_LABELS[s.paymentMethod]}
                    </span>
                  </span>
                  <SaleStatusBadge sale={s} />
                  <span className={cn('text-sm font-semibold tabular-nums', s.status === 'VOIDED' && 'line-through')}>
                    {formatARS(s.totalCents)}
                  </span>
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <SaleDetailDialog
        sale={selected}
        canVoid={!!selected && (isManager || selected.createdBy.id === user?.id)}
        onOpenChange={(o) => !o && setSelected(null)}
      />
    </Card>
  );
}

type Kind = 'expense' | 'income';

export function MovementDialog({
  kind,
  onOpenChange,
}: {
  kind: Kind | null;
  onOpenChange: (open: boolean) => void;
}) {
  const local = useCurrentLocal();
  const invalidate = useInvalidateCaja(local.id);
  const [amount, setAmount] = useState<number | null>(null);
  const [category, setCategory] = useState<ExpenseCategory>('INSUMOS');
  const [method, setMethod] = useState<PaymentMethod>('EFECTIVO');
  const [description, setDescription] = useState('');

  const reset = () => {
    setAmount(null);
    setCategory('INSUMOS');
    setMethod('EFECTIVO');
    setDescription('');
  };

  const save = useMutation({
    mutationFn: () => {
      const body = { amountCents: amount!, paymentMethod: method, description: description.trim() || null };
      return kind === 'expense'
        ? caja.createExpense(local.id, { ...body, category })
        : caja.createIncome(local.id, body);
    },
    onSuccess: () => {
      toast.success(kind === 'expense' ? 'Gasto cargado' : 'Ingreso cargado');
      void invalidate();
      reset();
      onOpenChange(false);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Dialog
      open={!!kind}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{kind === 'expense' ? 'Nuevo gasto' : 'Nuevo ingreso'}</DialogTitle>
          <DialogDescription>
            {kind === 'expense'
              ? 'Plata que sale: compras, pagos, retiros.'
              : 'Plata que entra y no es una venta: aportes, cobros varios.'}
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (amount && amount > 0) save.mutate();
          }}
        >
          <div className="grid gap-1.5">
            <Label htmlFor="mov-amount">Monto</Label>
            <MoneyInput id="mov-amount" value={amount} onChange={setAmount} autoFocus className="h-12 text-lg" />
          </div>
          {kind === 'expense' && (
            <div className="grid gap-1.5">
              <Label>Categoría</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as ExpenseCategory)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {EXPENSE_CATEGORY_LABELS[c]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid gap-1.5">
            <Label>Medio</Label>
            <PaymentMethodPicker value={method} onChange={setMethod} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="mov-desc">Detalle (opcional)</Label>
            <Input
              id="mov-desc"
              value={description}
              maxLength={200}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={kind === 'expense' ? 'Hielo, cucuruchos…' : 'Aporte de cambio'}
            />
          </div>
          <DialogFooter>
            <Button type="submit" size="lg" disabled={!amount || amount <= 0 || save.isPending}>
              {save.isPending && <Spinner />} Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type Movement = (ExpenseDto & { kind: 'expense' }) | (IncomeDto & { kind: 'income' });

export function TodayMovements({
  businessDate,
  onAdd,
}: {
  businessDate: string;
  /** Abre el diálogo para cargar un gasto o un ingreso. */
  onAdd?: (kind: 'expense' | 'income') => void;
}) {
  const local = useCurrentLocal();
  const { user } = useAuth();
  const isManager = useHasRole('OWNER', 'MANAGER');
  const invalidate = useInvalidateCaja(local.id);
  const query = { businessDate };
  const expenses = useQuery({
    queryKey: cajaKeys.expenses(local.id, query),
    queryFn: () => caja.listExpenses(local.id, query),
  });
  const incomes = useQuery({
    queryKey: cajaKeys.incomes(local.id, query),
    queryFn: () => caja.listIncomes(local.id, query),
  });
  const remove = useMutation({
    mutationFn: (m: Movement) => (m.kind === 'expense' ? caja.deleteExpense(local.id, m.id) : caja.deleteIncome(local.id, m.id)),
    onSuccess: () => {
      toast.success('Movimiento borrado');
      void invalidate();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const rows: Movement[] = [
    ...(expenses.data ?? []).map((e) => ({ ...e, kind: 'expense' as const })),
    ...(incomes.data ?? []).map((i) => ({ ...i, kind: 'income' as const })),
  ].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Gastos e ingresos</CardTitle>
      </CardHeader>
      <CardContent className="px-0">
        {expenses.isLoading || incomes.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            compact
            icon={ArrowLeftRight}
            title="Sin gastos ni ingresos hoy"
            description="Anotá acá lo que sale o entra de la caja que no sea una venta (proveedores, cambio, etc.)."
            action={
              onAdd && (
                <>
                  <Button size="sm" variant="outline" onClick={() => onAdd('expense')}>
                    Cargar gasto
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onAdd('income')}>
                    Cargar ingreso
                  </Button>
                </>
              )
            }
          />
        ) : (
          <ul className="divide-y">
            {rows.map((m) => {
              const canDelete = !m.cashCloseId && (isManager || m.createdBy.id === user?.id);
              const Icon = m.kind === 'expense' ? ArrowUpRight : ArrowDownLeft;
              return (
                <li key={`${m.kind}:${m.id}`} className="flex items-center gap-3 px-5 py-3">
                  <Icon className={cn('size-4 shrink-0', m.kind === 'expense' ? 'text-destructive' : 'text-success')} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">
                      {m.kind === 'expense' ? EXPENSE_CATEGORY_LABELS[m.category] : 'Ingreso'}
                      {m.description && <span className="text-muted-foreground"> · {m.description}</span>}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {formatTime(m.occurredAt, local.timezone)} · {PAYMENT_METHOD_LABELS[m.paymentMethod]} · {m.createdBy.name}
                    </span>
                  </span>
                  <span className="text-sm font-semibold tabular-nums">
                    {m.kind === 'expense' ? '−' : '+'}
                    {formatARS(m.amountCents)}
                  </span>
                  {canDelete && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="size-8"
                      aria-label="Borrar movimiento"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm('¿Borrar este movimiento?')) remove.mutate(m);
                      }}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
